import { doorForItem } from '@/services/queue.service.js';

// What actually needs the broker, on a deal he is already looking at.
//
// The SERVER already derives the answer and puts it in `section`
// (utils/queue-derive.js): `waiting` means "sent-and-quiet, read-mostly, the
// client or the carrier holds the ball." The deal-page surfaces were rendering
// all three sections undifferentiated under a heading that says "Needs you",
// so a submission sent this morning and quiet for zero days read as a task.
//
// Keith A Nichols / N8095M, 2026-08-05: FIVE items in "Needs you", of which
// ZERO were in section `now` — four were `waiting` and the fifth was an FYI.
// Every single one was a non-event. This is the third instance of the pattern
// after the stuck-bind alert and the queue, and the cost is always the same:
// a panel that fires on non-events gets ignored, and then the one real item
// in it is missed too.
//
// This module decides PRESENTATION ONLY and derives no new fact — the
// actionable/waiting split is the server's `section`. It lives in one file so
// the rail and the zone-2 panel cannot drift apart.

// Kinds that are good news rather than work. A PHF landing is something the
// client did FOR us; the deal's own Pilots panel already shows it, and nothing
// is required of the broker. Deliberately NOT extended to payment_cleared or
// signature_completed: both unblock a real next step (bind), so they stay.
// pilot_email_changed (ruling (a)2, 2026-10-17): the email on a pilot form
// changed and the old address was told; David is informed, nothing is asked.
// typed_email_matches_client (ruling (a)3): a typed pilot email is on another
// client's record; the pilot was added as a separate person, nothing linked.
const FYI_KINDS = new Set(['phf_submitted', 'pilot_email_changed', 'typed_email_matches_client']);

export function isFyi(item) {
  return !!item && FYI_KINDS.has(item.kind);
}

/** True when the ball is in the broker's court and there is something to do. */
export function needsBroker(item) {
  if (!item) return false;
  if (item.section === 'waiting') return false;
  return !isFyi(item);
}

/**
 * A door is only real if it goes somewhere else. `doorForItem` falls through
 * to "Open deal" → the deal href for every kind it has no specific door for
 * (phf_submitted, awaiting_client, market_waiting, the notification kinds).
 * On the deal page that href IS the current page, so the control renders,
 * clicks, and does nothing — which is exactly what David hit on
 * "PHF submitted: Keith Nichols".
 */
export function hasRealDoor(item, dealId, localDoors = {}) {
  if (!item) return false;
  if (localDoors[item.kind]) return true;
  if (item.kind === 'market_waiting') return true; // focuses the markets surface
  const door = doorForItem(item);
  if (!door) return false;
  if (!dealId) return true;
  const self = `/account/${encodeURIComponent(dealId)}/deal/${encodeURIComponent(dealId)}`;
  return door.href !== self;
}

const WAITING_PHRASE = {
  market_waiting: (n) => `${n} market${n === 1 ? '' : 's'} sent, awaiting response`,
  awaiting_client: (n) => `${n} quote${n === 1 ? '' : 's'} presented, client deciding`,
  // Decision 8: a re-quote note on a pilot change; it clears itself when the client picks the new quote.
  bind_held: (n) => `${n} pilot change${n === 1 ? '' : 's'} marked for a new quote`,
};

/**
 * Collapse the waiting rows to ONE line. Three rows that each link to the same
 * Markets page is clutter; the rail's Markets card below already lists every
 * carrier and its state, so the per-row detail is not lost by collapsing.
 * Returns null when nothing is waiting.
 */
export function waitingSummary(items) {
  const waiting = (items || []).filter((i) => i && i.section === 'waiting');
  if (waiting.length === 0) return null;
  const counts = {};
  for (const w of waiting) counts[w.kind] = (counts[w.kind] || 0) + 1;
  const parts = Object.entries(counts).map(([kind, n]) =>
    (WAITING_PHRASE[kind] || ((c) => `${c} waiting`))(n));
  return { text: parts.join(' · '), count: waiting.length, items: waiting };
}

/** { tasks, fyi, waiting } — tasks is what the heading is actually promising. */
export function partitionQueue(items) {
  const all = items || [];
  return {
    tasks: all.filter(needsBroker),
    fyi: all.filter(isFyi),
    waiting: all.filter((i) => i && i.section === 'waiting'),
  };
}
